import { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import * as yup from "yup";
import { StyledButton } from "../../../components/Button/button";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { ApplicationState } from "../../../redux/reducers";
import { ReactComponent as Warehouse } from "../../../assets/icons/warehouse.svg";
import { ReactComponent as WarehouseLeft } from "../../../assets/icons/warehouse-left.svg";
import Icon from "../../../components/icon";
import { theme } from "../../../helpers/theme";
import SessionService from "../../../services/session-service";
import {
  GetAllWarehouses,
  GetAllWarehousesPayload,
} from "../../../services/warehouses/get-all/types";

import { url, urlClient } from "../../../utils/url";
import { useNavigate } from "react-router-dom";
import OfferFormHeader from "../../common-views/offer-form/components/offer-form-header/offer-form-header";
import OfferLocationPopover from "../../common-views/offer-form/components/offer-location-popover/offer-location-popover";
import OfferDatePickers from "../../common-views/offer-form/components/offer-date-pickers/offer-date-pickers";
import ButtonsSection from "../../common-views/offer-form/components/buttons-section/buttons-section";
import CheckboxesSection from "../../common-views/offer-form/components/checkboxes-section/checkboxes-section";
import StorageTypeSection from "../../common-views/offer-form/components/storage-type-section/storage-type-section";
import PackageTypeChoose from "../../common-views/offer-form/components/package-type-choose/package-type-choose";
import PackageTypeSummary from "../../common-views/offer-form/components/package-type-summary/package-type-summary";
import AdditionalComments from "../../common-views/offer-form/components/additional-comments/additional-comments";
import CustomerData from "../../common-views/offer-form/components/customer-data/customer-data";
import PrivacyPolicy from "../../common-views/offer-form/components/privacy-policy/privacy-policy";
import SummaryButton from "../../common-views/offer-form/components/summary-button/summary-button";
import { useQuery } from "@tanstack/react-query";
import { getAllWarehouses } from "../../../services/warehouses/get-all";
import { LoadingBackDrop } from "../../../components/loader";

export const OfferFormLogged = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const body = {
    paginacja: null,
  };

  const { isLoading } = useQuery(["warehouses"], () => getAllWarehouses(body), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess(data) {
      dispatch(warehouses.fetchAllWarehouses(data.data.payload.magazyny));
      SessionService.setWarehousesList(data.data.payload.magazyny);
    },
  });

  const data = SessionService.getOrder();

  const orderStart = useAppSelector(
    (state: ApplicationState) => state.order.orderStart
  );
  const [element, setElement] = useState<string>(
    data?.miasto ? data.miasto : orderStart ? orderStart.miasto : ""
  );
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    data?.termin_rozpoczecia
      ? data.termin_rozpoczecia
      : orderStart
      ? orderStart.termin_rozpoczecia
      : null
  );
  const [toDate, setToDate] = useState<Dayjs | null>(
    data?.termin_zakonczenia
      ? data.termin_zakonczenia
      : orderStart
      ? orderStart.termin_zakonczenia
      : null
  );
  const [dateFromError, setDateFromError] = useState<string | null>(null);
  const [dateToError, setDateToError] = useState<string | null>(null);

  const [storageElement, setStorageElement] = useState<string>(
    data?.uslugi_zamowione.cel_zamowienia
      ? data.uslugi_zamowione.cel_zamowienia
      : orderStart
      ? orderStart.uslugi_zamowione.cel_zamowienia
      : "Składowanie"
  );
  const [additionalServices, setAdditionalServices] = useState<string[]>(
    data?.uslugi_zamowione.uslugi_dodatkowe
      ? data.uslugi_zamowione.uslugi_dodatkowe
      : orderStart
      ? orderStart.uslugi_zamowione.uslugi_dodatkowe
      : []
  );

  const [place, setPlace] = useState<string>(
    data?.miejsce ? data.miejsce : "Wewnątrz"
  );

  const [storageType, setStorageType] = useState<string>(
    data?.rodzaj_skladowania ? data.rodzaj_skladowania : "Wysokie"
  );

  const [temperatureConditions, setTemperatureConditions] = useState<string>(
    data?.warunek_temperaturowy ? data.warunek_temperaturowy : "Ogrzewane"
  );

  const [magazineType, setMagazineType] = useState<string>("Standardowy");

  const [goodsType, setGoodsType] = useState<string>(
    data?.rodzaj_towaru ? data.rodzaj_towaru : "Neutralny"
  );
  const [specialEquipment, setSpecialEquipment] = useState<string>(
    data?.specjalny_sprzet
      ? data?.specjalny_sprzet !== ""
        ? "Tak"
        : "Nie"
      : "Nie"
  );
  const [packageType, setPackageType] = useState<string>(
    data?.rodzaj_opakowania ? data.rodzaj_opakowania : "Paleta EUR"
  );
  const [privacyPolitycyAccept, setPrivacyPolitycyAccept] = useState(
    data?.regulamin ? data.regulamin : false
  );
  const warehouses = SessionService.getWarehousesList();
  const chooseWarehouse: GetAllWarehouses = useMemo(
    () =>
      warehouses.find(
        (item: GetAllWarehouses) => item.adres.miejscowosc === element
      ),
    [warehouses]
  );

  const validationSchema = yup.object({
    additionalServicesDescription: yup
      .string()
      .max(500, "Maksymalnie 500 znaków"),
    additionalServicesQuantity: yup
      .number()
      .min(1, "Minimalna wartość to 1")
      .typeError("Błędny format!")
      .required("Ilość jest wymagana"),
    goodsTypeDescription: yup.string().max(500, "Maksymalnie 500 znaków"),
    specialEquipmentDescription: yup
      .string()
      .max(500, "Maksymalnie 500 znaków"),
    additionalComments: yup.string().max(500, "Maksymalnie 500 znaków"),

    quantity: yup
      .number()
      .min(1, "Minimalna wartość to 1")
      .typeError("Błędny format!")
      .required("Ilość jest wymagana"),
    weight: yup
      .number()
      .min(1, "Minimalna wartość to 1")
      .typeError("Błędny format!")
      .required("Waga jest wymagana"),
  });

  const formik = useFormik({
    initialValues: {
      additionalServicesDescription: data?.opis_uslugi ? data.opis_uslugi : "",
      additionalServicesQuantity: data?.uslugi_zamowione.ilosc_dodatkowych
        ? data?.uslugi_zamowione.ilosc_dodatkowych
        : 1,
      goodsTypeDescription: data?.opis_towaru ? data.opis_towaru : "",
      specialEquipmentDescription: data?.specjalny_sprzet
        ? data.specjalny_sprzet
        : "",
      additionalComments: data?.dodatkowe_uwagi ? data.dodatkowe_uwagi : "",
      quantity: data?.ilosc ? data.ilosc : 1,
      weight: data?.waga ? data.waga / data.ilosc : 1,
      lengthEUR: 1200,
      widthEUR: 800,
      lengthEUR2: 800,
      widthEUR2: 600,
      height: 1600,
    },

    validationSchema: validationSchema,
    onSubmit: (values) => {
      const order = {
        opis_uslugi: values.additionalServicesDescription,
        termin_rozpoczecia: fromDate,
        termin_zakonczenia: toDate,
        kraj: chooseWarehouse.adres.kraj,
        wojewodztwo: chooseWarehouse.adres.wojewodztwo,
        kod_pocztowy: chooseWarehouse.adres.kod_pocztowy,
        miasto: element,
        szerokosc_geograficzna: chooseWarehouse.adres.szerokosc_geograficzna,
        dlugosc_geograficzna: chooseWarehouse.adres.dlugosc_geograficzna,
        miejsce: place, //TODO obecnie string będzie eumuem
        rodzaj_skladowania: storageType, //opcje: wysokie/ niskie/ półki
        warunek_temperaturowy: temperatureConditions,
        rodzaj_towaru: goodsType,
        opis_towaru: values.goodsTypeDescription,
        specjalny_sprzet:
          specialEquipment === "Tak" ? values.specialEquipmentDescription : "",
        dodatkowe_uwagi: values.additionalComments, //
        uslugi_zamowione: {
          cel_zamowienia: storageElement, //todo string
          uslugi_dodatkowe: additionalServices,
          ilosc_dodatkowych:
            additionalServices.length > 0
              ? values.additionalServicesQuantity
              : null,
        },
        rodzaj_opakowania: packageType,
        ilosc: values.quantity,
        waga: values.quantity * values.weight,
        dlugosc:
          packageType === "Paleta EUR" ? values.lengthEUR : values.lengthEUR2,
        szerokosc:
          packageType === "Paleta EUR" ? values.widthEUR : values.widthEUR2,
        wysokosc: values.height,
        rozmiar:
          packageType === "Paleta EUR"
            ? (values.lengthEUR * values.widthEUR * values.quantity) / 1000000
            : (values.lengthEUR2 * values.widthEUR2 * values.quantity) /
              1000000,
        klient: {
          info_dodatkowe: values.additionalComments,
        },
        regulamin: privacyPolitycyAccept,
      };
      SessionService.setOrder(order);
      navigate(`${urlClient.orderFormSummary}`);
    },
  });
  const isDisabled = useMemo(
    () =>
      (additionalServices.includes("Inne")
        ? formik.values.additionalServicesDescription && true
        : true) &&
      (goodsType === "Inne" && true
        ? formik.values.goodsTypeDescription && true
        : true) &&
      (specialEquipment === "Tak"
        ? formik.values.specialEquipmentDescription && true
        : true) &&
      element !== "" &&
      fromDate &&
      toDate &&
      !dateFromError &&
      !dateToError &&
      formik.isValid
        ? false
        : true,
    [
      additionalServices,
      goodsType,
      specialEquipment,
      element,
      fromDate,
      toDate,
      dateFromError,
      dateToError,
      formik.isValid,
      formik.values,
    ]
  );

  return (
    <PageContainer>
      <OfferFormWrapper onSubmit={formik.handleSubmit}>
        <OfferFormHeader continueOrder={orderStart || data} />
        <OfferLocationPopover element={element} setElement={setElement} />
        <OfferDatePickers
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          setDateFromError={setDateFromError}
          setDateToError={setDateToError}
        />
        <ButtonsSection
          currentValue={storageElement}
          setCurrentValue={setStorageElement}
          buttonsOptions={["Składowanie", "Crossdocking", "Fullfilment"]}
          popoverLabel="Czego szukasz?"
          popoverContent="Lorem Ipsum"
        />
        <CheckboxesSection
          currentValues={additionalServices}
          setCurrentValues={setAdditionalServices}
          checkboxesOptions={[
            "Etykietowanie",
            "Pakowanie",
            "Sortowanie",
            "E-commerce",
            "Wysyłka",
            "Inne",
          ]}
          formik={formik}
          helperFieldDisplay={additionalServices.includes("Inne") && true}
          helperFieldName="additionalServicesDescription"
          popoverLabel="Usługi dodatkowe"
          popoverContent="Lorem Ipsum"
        />

        <ButtonsSection
          currentValue={place}
          setCurrentValue={setPlace}
          buttonsOptions={["Wewnątrz", "Na zewnątrz"]}
          popoverLabel="Miejsce"
          popoverContent="Lorem Ipsum"
        />
        <StorageTypeSection
          currentValue={storageType}
          setCurrentValue={setStorageType}
          checkboxesOptions={["Wysokie", "Płaskie", "Półki"]}
          popoverLabel="Rodzaj składowania"
          popoverContent="Lorem Ipsum"
        />
        <ButtonsSection
          currentValue={temperatureConditions}
          setCurrentValue={setTemperatureConditions}
          buttonsOptions={[
            "Ogrzewane",
            "Nieogrzewane",
            "Chłodnia",
            "Kontrola temperatury",
          ]}
          popoverLabel="Warunki temperaturowe"
          popoverContent="Lorem Ipsum"
        />
        <ButtonsSection
          currentValue={magazineType}
          setCurrentValue={setMagazineType}
          buttonsOptions={["Standardowy"]}
          popoverLabel="Rodzaj magazynu"
          popoverContent="Lorem Ipsum"
        />
        <ButtonsSection
          currentValue={goodsType}
          setCurrentValue={setGoodsType}
          buttonsOptions={[
            "Neutralny",
            "Medyczne",
            "Art. Spożywcze",
            "Ciężkie ADR",
            "Inne",
          ]}
          formik={formik}
          helperFieldDisplay={goodsType === "Inne" && true}
          helperFieldName="goodsTypeDescription"
          popoverLabel="Rodzaj towarów"
          popoverContent="Lorem Ipsum"
        />
        <ButtonsSection
          currentValue={specialEquipment}
          setCurrentValue={setSpecialEquipment}
          buttonsOptions={["Nie", "Tak"]}
          formik={formik}
          helperFieldDisplay={specialEquipment === "Tak" && true}
          helperFieldName="specialEquipmentDescription"
          popoverLabel="Specjalny sprzęt"
          popoverContent="Lorem Ipsum"
        />
        <PackageTypeChoose
          currentValue={packageType}
          setCurrentValue={setPackageType}
          checkboxesOptions={["Paleta EUR", "Paleta EUR 1/2"]}
          popoverLabel="Rodzaj opakowania/nośnika"
          popoverContent="Lorem Ipsum"
        />
        <PackageTypeSummary packageType={packageType} formik={formik} />
        <AdditionalComments formik={formik} />

        <PrivacyPolicy
          privacyPolitycyAccept={privacyPolitycyAccept}
          setPrivacyPolitycyAccept={setPrivacyPolitycyAccept}
        />
        <SummaryButton isDisabled={isDisabled || !privacyPolitycyAccept} />
      </OfferFormWrapper>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding-bottom: 80px;
  align-items: flex-start;
  width: 100%;
  display: flex;
  justify-content: center;
`;
const OfferFormWrapper = styled.form`
  width: 74%;
  @media (min-width: 1400px) {
    padding: 0px 40px;
  }
  @media ${theme.device.Mlaptop} {
    width: 100%;
    padding: 0 20px;
  }
  max-width: 628px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  //padding: 0 20px;
`;
const IconWrapper = styled.div`
  @media (max-width: 1400px) {
    display: none;
  }
`;
